import React, { useEffect, useState } from 'react'
import { AppLayout } from '../components/layout/AppLayout'
import { Button } from '../components/ui/foundation'
import { FormSelect } from '../components/ui/foundation/FormSelect'
import {
  inlineLinkClass,
  kickerLabelClass,
  secondaryCardClass,
  secondaryInputClass,
  secondaryLabelClass,
} from '../components/secondary-pages/styles'
import { logError } from '../utils/logger'
import { useAuth } from '../hooks/useAuth'
import { useUserProfile } from '../hooks/useUserProfile'
import { Check } from '@phosphor-icons/react'
import { MetaTags } from '../components/seo/MetaTags'

const supportInputClass = `${secondaryInputClass} h-11`

const supportTextareaClass = `${supportInputClass} min-h-[120px] py-3 resize-none`

// Form data interface
interface SupportFormData {
  name: string
  email: string
  category:
    | 'resolve-issue'
    | 'contact-support'
    | 'feature-request'
    | 'learn-more'
    | ''
  subject: string
  message: string
}

type SupportCategoryOption = {
  value: SupportFormData['category']
  label: string
}

// Support categories
const supportCategories: SupportCategoryOption[] = [
  {
    value: 'resolve-issue',
    label: 'Resolve an Issue',
  },
  {
    value: 'contact-support',
    label: 'Contact Support',
  },
  {
    value: 'feature-request',
    label: 'Request a Feature',
  },
  {
    value: 'learn-more',
    label: 'Learn More',
  },
]

const categoryLabelMap = new Map(
  supportCategories.map((category) => [category.value, category.label])
)

export const ContactSupportPage: React.FC = () => {
  const { user } = useAuth()
  const { profile } = useUserProfile()
  const [formData, setFormData] = useState<SupportFormData>({
    name: '',
    email: '',
    category: '',
    subject: '',
    message: '',
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [hasInitializedProfileFields, setHasInitializedProfileFields] =
    useState(false)

  useEffect(() => {
    if (hasInitializedProfileFields) return
    if (!user && !profile) return

    setFormData((prev) => ({
      ...prev,
      name:
        prev.name || profile?.display_name || user?.email?.split('@')[0] || '',
      email: prev.email || profile?.email || user?.email || '',
    }))
    setHasInitializedProfileFields(true)
  }, [hasInitializedProfileFields, profile, user])

  const handleInputChange = <K extends keyof SupportFormData>(
    field: K,
    value: SupportFormData[K]
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    setErrorMessage(null)

    try {
      const response = await fetch('/.netlify/functions/send-contact-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          category: formData.category,
          subject: formData.subject,
          message: formData.message,
        }),
      })

      const result = await response.json()

      if (!response.ok) {
        throw new Error(result.error || 'Failed to send message')
      }

      setIsSubmitted(true)
    } catch (error) {
      logError('ContactSupportPage', 'Contact form error:', error)
      setErrorMessage(
        error instanceof Error
          ? error.message
          : 'Failed to send message. Please try again or email us directly.'
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  const isFormValid =
    formData.name && formData.email && formData.category && formData.message

  const pageContent = (
    <div className="flex justify-center px-4 py-16 sm:py-20">
      <div className="w-full max-w-[540px]">
        <div className="text-center mb-8">
          <p className={kickerLabelClass}>SUPPORT</p>
          <h1 className="mt-3 font-serif text-fs-700 font-semibold tracking-tight text-ink-700">
            We're here for you.
          </h1>
          <p className="mt-3 font-sans text-fs-400 font-normal text-ink-500 max-w-[400px] mx-auto">
            Tell us what's on your mind. We'll get back to you within 24 hours.
          </p>
        </div>

        <div className={`${secondaryCardClass} p-8`}>
          {isSubmitted ? (
            <div className="text-center py-8">
              <div className="w-12 h-12 bg-secondary-400 rounded-full mx-auto mb-4 flex items-center justify-center">
                <Check className="w-6 h-6 text-white" />
              </div>
              <h2 className="text-lg font-semibold text-ink-700 mb-1">
                Message sent
              </h2>
              <p className="text-sm font-normal leading-relaxed text-ink-500">
                We'll get back to you within 24 hours.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              {errorMessage && (
                <div className="bg-error-100 border border-[rgba(181,112,106,0.25)] rounded-xl px-4 py-3 text-fs-300 text-error-500">
                  {errorMessage}
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="contact-name" className={secondaryLabelClass}>
                    Name
                  </label>
                  <input
                    id="contact-name"
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    placeholder="Your name"
                    required
                    autoComplete="name"
                    className={supportInputClass}
                  />
                </div>
                <div>
                  <label
                    htmlFor="contact-email"
                    className={secondaryLabelClass}
                  >
                    Email
                  </label>
                  <input
                    id="contact-email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    placeholder="you@example.com"
                    required
                    autoComplete="email"
                    inputMode="email"
                    className={supportInputClass}
                  />
                </div>
              </div>

              <div>
                <label
                  htmlFor="support-category"
                  className={secondaryLabelClass}
                >
                  How can we help?
                </label>
                <FormSelect
                  id="support-category"
                  value={formData.category}
                  onValueChange={(value) => {
                    const nextValue = value as SupportFormData['category']
                    const previousLabel =
                      categoryLabelMap.get(formData.category) ?? ''
                    const nextLabel = categoryLabelMap.get(nextValue) ?? ''

                    setFormData((prev) => ({
                      ...prev,
                      category: nextValue,
                      subject:
                        !prev.subject || prev.subject === previousLabel
                          ? nextLabel
                          : prev.subject,
                    }))
                  }}
                  options={supportCategories}
                  placeholder="Select a topic"
                />
              </div>

              <div>
                <label
                  htmlFor="contact-subject"
                  className={secondaryLabelClass}
                >
                  Subject
                </label>
                <input
                  id="contact-subject"
                  type="text"
                  value={formData.subject}
                  onChange={(e) => handleInputChange('subject', e.target.value)}
                  placeholder="Brief description"
                  className={supportInputClass}
                />
              </div>

              <div>
                <label
                  htmlFor="contact-message"
                  className={secondaryLabelClass}
                >
                  Message
                </label>
                <textarea
                  id="contact-message"
                  value={formData.message}
                  onChange={(e) => handleInputChange('message', e.target.value)}
                  placeholder="Tell us more..."
                  required
                  rows={4}
                  className={supportTextareaClass}
                />
              </div>

              <Button
                type="submit"
                variant="primary"
                disabled={!isFormValid || isSubmitting}
                className="w-full rounded-xl py-3 mt-2"
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </Button>
            </form>
          )}
        </div>

        {/* Minimal contact info */}
        <p className="text-center text-sm font-normal leading-relaxed text-ink-400 mt-6">
          Or email us directly at{' '}
          <a href="mailto:contact@printboard.io" className={inlineLinkClass}>
            contact@printboard.io
          </a>
        </p>
      </div>
    </div>
  )

  return (
    <AppLayout>
      <MetaTags
        title="Support | Printboard"
        description="Get help with your account, orders, or board experience."
        noindex={true}
      />
      {pageContent}
    </AppLayout>
  )
}

export default ContactSupportPage
